import { EventDesignPurpose, useGuestSiteWebsiteHeaderSettingsQuery } from '@graphql/generated';
import useWebsiteHeaderUpdatesListener from '../useWebsiteHeaderUpdatesListener/useSubscribeWebsiteHeaderUpdates';
import { isInIframe } from '@shared/utils/isInIframe';
import {
  AlohaWebsiteHeaderSettings,
  BrannanWebsiteHeaderSettings,
  SupportedAlohaHeaderPhotoLayout,
  SupportedAlohaHeaderPhotoLayoutSchema,
  SupportedBrannanHeaderPhotoLayout,
  SupportedBrannanHeaderPhotoLayoutSchema,
} from './useWebsiteHeaderSettings.types';
import { isValidSchema } from './headerSchemaValidator';
import { useQueryParams } from '@shared/utils/hooks/useQueryParams';
import { decodeData } from '@shared/utils/encodeData';
import { useWebsitePublishCapabilityFeature } from '@apps/admin/routes/WebsiteDesignerV2/hooks/useWebsitePublishCapabilityFeature';

type UseWebsiteHeaderSettingsArgs = {
  eventHandle: string;
  targetWebsiteLayout: 'aloha' | 'brannan';
  presentationLayoutOverride?: SupportedAlohaHeaderPhotoLayout | SupportedBrannanHeaderPhotoLayout;
  /**
   * Only intended for use in local develoment.
   */
  forceDisable?: boolean;
};

const NO_OP_DEFAULT_RETURN = {
  isLoading: false,
  websiteHeader: null,
};

/**
 * Stubbed hook to return website header settings based on the provided layout and presentation type.
 *
 *  @todo
 * - Add schema validation https://withjoy.atlassian.net/browse/PRNT-2869
 */
export function useWebsiteHeaderSettings(args: { eventHandle: string; targetWebsiteLayout: 'aloha' | 'brannan'; forceDisable: true }): {
  isLoading: boolean;
  websiteHeader: null;
};
export function useWebsiteHeaderSettings(args: { targetWebsiteLayout: 'aloha'; eventHandle: string }): AlohaWebsiteHeaderSettings;
export function useWebsiteHeaderSettings(args: { targetWebsiteLayout: 'brannan'; eventHandle: string }): BrannanWebsiteHeaderSettings;
export function useWebsiteHeaderSettings(args: UseWebsiteHeaderSettingsArgs): BrannanWebsiteHeaderSettings | AlohaWebsiteHeaderSettings {
  const { forceDisable, targetWebsiteLayout } = args;

  const shouldSkip = forceDisable;

  const { eventExposure } = useWebsitePublishCapabilityFeature();
  const isPreviewing = isInIframe();
  const shouldUseLivePurpose = eventExposure.value || !isPreviewing;

  const { data, loading: isFetchingWebsiteHeaderSettings } = useGuestSiteWebsiteHeaderSettingsQuery({
    skip: shouldSkip || eventExposure.isLoading,
    variables: { eventHandle: args.eventHandle, eventDesignPurpose: shouldUseLivePurpose ? EventDesignPurpose.live : EventDesignPurpose.draft },
    batchMode: 'off',
    // SSR is required for fixing mobile art rendering issue.
    // We need the value of websiteHeader at the time of first render itself for proper style application by the style applicator.
    ssr: true,
  });

  // Query param to override the website header settings
  const { templateData } = useQueryParams();

  const eventData = data?.eventByName;
  const eventDesign = eventData?.eventDesign;
  const activeHeaderPresentationLayout = eventDesign?.activeWebsiteHeaderPresentationLayout;
  const { overrides } = useWebsiteHeaderUpdatesListener({
    initialValues: {
      activeHeaderPresentationLayout,
      websiteLayout: eventDesign?.websiteLayout.layoutType,
      eventDisplayName: eventData?.info.eventDisplayName,
      greetingMessage: eventData?.info.greeting,
    },
  });

  if (shouldSkip) {
    return NO_OP_DEFAULT_RETURN;
  }

  if (!eventDesign || !eventData) {
    return {
      isLoading: isFetchingWebsiteHeaderSettings,
      websiteHeader: null,
    };
  }

  let consolidatedActiveHeaderPresentationLayout = overrides.activeHeaderPresentationLayout || activeHeaderPresentationLayout;
  const consolidatedWebsiteLayout = overrides.websiteLayout || eventDesign.websiteLayout.layoutType;
  const consolidatedEventDisplayName = overrides.eventDisplayName || eventData.info.eventDisplayName;
  const consolidatedGreetingMessage = overrides.greetingMessage || eventData.info.greeting;

  if (templateData && typeof templateData === 'string') {
    const activeWebsiteHeaderPresentationLayoutForPreview = decodeData<{ activeWebsiteHeaderPresentationLayout: typeof consolidatedActiveHeaderPresentationLayout }>(
      templateData,
    )?.activeWebsiteHeaderPresentationLayout;
    if (activeWebsiteHeaderPresentationLayoutForPreview) {
      consolidatedActiveHeaderPresentationLayout = activeWebsiteHeaderPresentationLayoutForPreview;
    }
  }

  if (!consolidatedActiveHeaderPresentationLayout || targetWebsiteLayout !== consolidatedActiveHeaderPresentationLayout.websiteLayout || !consolidatedWebsiteLayout) {
    return {
      isLoading: isFetchingWebsiteHeaderSettings,
      websiteHeader: null,
    };
  }

  // Add this back once API is ready
  // if (!isFetchingWebsiteHeaderSettings && !eventData?.eventDesign?.websiteHeaderPresentationLayout) {
  //  return {
  //    isLoading: false,
  //    websiteHeader: null
  //  };
  // }

  // Need to do an explicit if/else, TSC has difficulty with union type discrimination in this case
  if (
    consolidatedWebsiteLayout === 'aloha' &&
    isValidSchema<SupportedAlohaHeaderPhotoLayoutSchema>(
      consolidatedWebsiteLayout,
      consolidatedActiveHeaderPresentationLayout.schemaURL,
      consolidatedActiveHeaderPresentationLayout.dataJSON,
    )
  ) {
    return {
      websiteHeader: {
        eventDisplayName: consolidatedEventDisplayName,
        greetingMessage: consolidatedGreetingMessage,
        headerPhoto: consolidatedActiveHeaderPresentationLayout.dataJSON,
      },

      isLoading: isFetchingWebsiteHeaderSettings,
    };
  } else if (
    consolidatedWebsiteLayout === 'brannan' &&
    isValidSchema<SupportedBrannanHeaderPhotoLayoutSchema>(
      consolidatedWebsiteLayout,
      consolidatedActiveHeaderPresentationLayout.schemaURL,
      consolidatedActiveHeaderPresentationLayout.dataJSON,
    )
  ) {
    return {
      websiteHeader: {
        eventDisplayName: consolidatedEventDisplayName,
        headerPhoto: consolidatedActiveHeaderPresentationLayout.dataJSON,
      },

      isLoading: isFetchingWebsiteHeaderSettings,
    };
  }

  return NO_OP_DEFAULT_RETURN;
}
